import Axios from "axios";
import * as Sentry from "@sentry/react";
import sessionExpired from "../Core/sessionExpired";
import { logAuthError } from "./logAuthError";
import {
  getTokenObj,
  isMobileApp,
  isCySsoToken,
  isNewerToken,
  isTokenFresh,
  tokenUserId,
} from "./tokenManager";
import { getTokenRefreshHandler } from "../mfe/tokenRefreshBridge";
import {
  refreshTokens,
  isGenuineRefreshFailure,
  isRefreshRaceLost,
} from "./refreshTokens";
import { saveSession } from "../Core/utils/auth";
import { isLoggedOut, getCookie } from "../Core/utils/cookieUtils";
import { FeatureFlag } from "../featureFlags/activeFlags";
import { FLAGS } from "../featureFlags/registry";
import { LOGOUT_REASONS, refreshFailureDetails } from "../utils/authEventLog";
import { recoveryCheckDetails, logSessionRecovered } from "./recoveryDiagnostics";

// The endpoint a request hit, without its query string, for the logout log.
const requestPath = (config) => String(config?.url || "").split("?")[0];

let cancelTokensMap = {};

// Fire sessionExpired at most once per page life — repeated 401s (a dashboard
// fires many at once) must not stack multiple redirects.
let sessionEnded = false;

// Shared across ALL createApiClient instances so only one refresh call fires
// even when multiple API instances (API, API_EPICENTER, API_STUDENT_PORTAL, …)
// simultaneously receive a 401.
let isRefreshing = false;
let refreshQueue = [];

// ── Session-expiry (strict: ZERO tolerance after a refresh) ─────────────────
// Logout happens when the session can't continue, two ways:
//   (a) the REFRESH fails for a GENUINE reason — the server responds (401 invalid
//       tokens, 500, or 200 with valid:false). A pure connectivity error (offline /
//       no `response`) does NOT log out — the session is kept and a later request
//       refreshes once the user is back online. (See refreshTokens.js →
//       isGenuineRefreshFailure.) This fixes the 500-refresh "stuck" bug without
//       logging out users who merely lost internet.
//   (b) ANY request still 401s AFTER a successful refresh → with a fresh token
//       every API should work, so a 401 now means the session is dead → logout.
// No counting, no tolerance. A normal request's 403 (or other non-401) is a
// permission/forbidden error and never logs out — only a 401 enters the refresh
// path above.
//
// EXCEPTION — lost refresh race (`recentDeletion` → isRefreshRaceLost): the
// session is ALIVE, another tab/app just rotated the tokens first. Never a
// logout; recovered by adoptTokensFromSiblingContext below. Without this, every
// concurrent refresh across two tabs (or portal + standalone sandbox) logged
// the losing context out — and its sessionExpired() cascade (cy_logged_out +
// cookie wipe + localStorage.clear) then tore down the WINNING context too.

const storeExpiredApis = (cb) => {
  refreshQueue.push(cb);
};

const onRefreshed = (accessToken, refreshToken) => {
  refreshQueue.forEach((cb) => cb(accessToken, refreshToken, null));
  refreshQueue = [];
};

const onRefreshFailed = (err) => {
  refreshQueue.forEach((cb) => cb(null, null, err));
  refreshQueue = [];
};

// ── Lost-refresh-race recovery ──────────────────────────────────────────────
// With rotating refresh tokens, one session lineage is shared by several JS
// contexts (other tabs of this app via localStorage; the other app via the
// shared cookies). Refresh dedupe (`isRefreshing` / the MFE coordinator) only
// works INSIDE one page, so two contexts can race a refresh — the backend
// rotates for the winner and answers the loser with `recentDeletion`
// (surfaced here as `isRefreshRaceLost`). The loser must NOT log out: the
// session is alive, the winner holds the fresh pair. This helper polls the
// sibling stores for a pair that is strictly NEWER (JWT iat) than the pair
// whose refresh failed, for the SAME user, and adopts it.
// Returns { accessToken, refreshToken } or null.

const RACE_RECOVERY_ATTEMPTS = 3;
const RACE_RECOVERY_DELAY_MS = 700;

// ── Stale-tab recovery (auth_stale_tab_recovery) ────────────────────────────
// The race recovery above only runs on cy-sso's `recentDeletion` signal, which
// lasts ONE MINUTE after a rotation. A tab that stays focused never fires the
// focus/visibility cookie resync either (authSyncListener), so when the other
// app rotates the tokens and this tab's next request comes more than a minute
// later, its refresh is answered with a plain 401 — indistinguishable from a
// dead session, although the live pair is sitting in the shared cookies.
// With the flag ON the same sibling-store check runs (once, no polling — the
// winner persisted long ago) before the refresh call, which then refreshes
// with the adopted pair, and again if cy-sso rejects the refresh. A genuine
// logout still logs out: clearAuthCookies() wipes the shared cookies, so there
// is nothing newer to adopt.
const staleTabRecoveryOn = () =>
  FeatureFlag.isFeatureActive(FLAGS.AUTH_STALE_TAB_RECOVERY.name);

const adoptTokensFromSiblingContext = async (
  failedAccessToken,
  attempts = RACE_RECOVERY_ATTEMPTS
) => {
  for (let attempt = 0; attempt < attempts; attempt++) {
    // (a) Same-origin sibling tab: it wrote the fresh pair to localStorage.
    const stored = getTokenObj();
    if (
      stored?.ACCESS_TOKEN &&
      stored.ACCESS_TOKEN !== failedAccessToken &&
      isNewerToken(stored.ACCESS_TOKEN, failedAccessToken) &&
      tokenUserId(stored.ACCESS_TOKEN) === tokenUserId(failedAccessToken)
    ) {
      return {
        accessToken: stored.ACCESS_TOKEN,
        refreshToken: stored.REFRESH_TOKEN,
        source: "localStorage",
      };
    }

    // (b) The other app (cross-origin): its refresh re-wrote the shared
    // cookies. Same-user + strictly-newer only — a DIFFERENT user's cookie is
    // a profile switch, owned by authSyncListener/bootstrap, not by recovery.
    const cookieAccess = getCookie("Authorization");
    const cookieRefresh = getCookie("refresh-token");
    if (
      cookieAccess &&
      cookieRefresh &&
      cookieAccess !== failedAccessToken &&
      isNewerToken(cookieAccess, failedAccessToken) &&
      tokenUserId(cookieAccess) === tokenUserId(failedAccessToken)
    ) {
      const prevUser = getTokenObj() || {};
      saveSession({
        accessToken: cookieAccess,
        refreshToken: cookieRefresh,
        userData: prevUser,
      });
      return { accessToken: cookieAccess, refreshToken: cookieRefresh, source: "cookie" };
    }

    // Winner may not have persisted yet — wait and re-check.
    if (attempt < attempts - 1) {
      await new Promise((resolve) => setTimeout(resolve, RACE_RECOVERY_DELAY_MS));
    }
  }
  return null;
};

// ── Cross-tab refresh lock ──────────────────────────────────────────────────
// Tabs of this origin share one session in localStorage, and cy-sso rotates the
// refresh token on every refresh. Two tabs that refresh at the same moment make
// the second one lose: cy-sso answers it with `recentDeletion`, and the winner's
// new pair may not be stored yet when the loser looks for it. So the refresh
// runs under a Web Lock: one tab at a time. A tab that gets the lock after
// another tab's refresh finds that tab's pair in localStorage and uses it
// without calling cy-sso (see the check at the start of the locked section).
// Browsers without Web Locks, and a lock that stays held (a frozen tab), fall
// back to refreshing without it, as before.
const REFRESH_LOCK_NAME = "sandbox-token-refresh";
const REFRESH_LOCK_WAIT_MS = 10000;

const withRefreshLock = async (task) => {
  const locks = typeof navigator !== "undefined" ? navigator.locks : null;
  if (!locks || typeof locks.request !== "function") return task();

  const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
  const timer = controller ? setTimeout(() => controller.abort(), REFRESH_LOCK_WAIT_MS) : null;
  try {
    return await locks.request(
      REFRESH_LOCK_NAME,
      controller ? { signal: controller.signal } : {},
      () => {
        clearTimeout(timer);
        return task();
      }
    );
  } catch (lockError) {
    // Only the wait timeout aborts the request; an error from the task itself
    // is not an AbortError and goes on to the caller.
    if (lockError?.name === "AbortError") return task();
    throw lockError;
  } finally {
    clearTimeout(timer);
  }
};

// The access token a request was sent with.
const requestAccessToken = (config) =>
  String(config?.headers?.Authorization || "").replace(/^Bearer\s+/i, "");

const createApiClient = function (baseUrl) {
  this.apiBase = baseUrl;
  this.axios = Axios.create({
    baseURL: this.apiBase,
    timeout: 300000,
  });

  this.axios.defaults.headers.post["Content-Type"] = "application/json";

  this.axios.interceptors.request.use(
    (req) => {
      const tokenObj = getTokenObj();
      const accessToken = tokenObj?.ACCESS_TOKEN;
      // Legacy (pre-cy-sso) tokens lack `accessTokenSecretId`. Token migration
      // has been removed — log these users out so they re-login and get a fresh
      // cy-sso token. (Sending the old token 401s and refreshing it 500s, which
      // left old users stuck — never logged out.) Only acts when a token exists,
      // so login / public pages are unaffected.
      if (accessToken && !isCySsoToken(accessToken)) {
        if (!sessionEnded) {
          sessionEnded = true;
          sessionExpired(LOGOUT_REASONS.LEGACY_TOKEN);
        }
        return Promise.reject(new Error("LEGACY_SESSION_LOGOUT"));
      }
      req.headers["Authorization"] = `Bearer ${accessToken || ""}`;
      req.headers["refresh-token"] = tokenObj?.REFRESH_TOKEN || "";
      req.headers["auth-source"] = "cy-sso";
      return req;
    },
    (e) => Promise.reject(e)
  );

  this.axios.interceptors.response.use(
    (res) => res,
    async (error) => {
      const originalRequest = error.config;

      // NOTE: 403 (and every other non-401 status) is intentionally NOT a
      // logout — a 403 is a permission/forbidden error, not session expiry.

      // Deterministic cross-app logout: a STUDENT logged out / expired in the
      // other app (portal ⇄ sandbox) and raised the shared `cy_logged_out`
      // cookie. On ANY 401 here, trust that signal and end the session
      // immediately — no fragile 401-counting, no refresh attempt that would
      // just spin the page. Guarded so it fires once, and only when we actually
      // have a local session (avoids a redirect loop on the public login page).
      // STUDENT-ONLY, symmetric with setLoggedOutFlag: mentor/admin sessions are
      // app-local and must never be torn down by a student's cross-app logout
      // signal (e.g. a student logging out elsewhere on a shared browser).
      const localRole = getTokenObj()?.role;
      if (
        error.response?.status === 401 &&
        isLoggedOut() &&
        localStorage.getItem("sandbox_user") &&
        (!localRole || localRole === "student") &&
        !sessionEnded
      ) {
        sessionEnded = true;
        sessionExpired(LOGOUT_REASONS.LOGGED_OUT_IN_OTHER_TAB, {
          trigger: "api-401",
          endpoint: requestPath(originalRequest),
        });
        return Promise.reject(error);
      }

      if (error.response?.status === 401 && !originalRequest._retry) {
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            storeExpiredApis((accessToken, refreshToken, err) => {
              if (err) return reject(err);
              // Mark queued retries too, so a still-401 retry is counted by the
              // forgiving threshold below instead of kicking off a 2nd refresh.
              originalRequest._retry = true;
              originalRequest.headers["Authorization"] = "Bearer " + accessToken;
              originalRequest.headers["refresh-token"] = refreshToken;
              resolve(this.axios(originalRequest));
            });
          });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        // Snapshot the token whose refresh we're about to attempt — the
        // race-lost recovery below compares sibling stores against it.
        let failedAccessToken = getTokenObj()?.ACCESS_TOKEN;
        // For the diagnostics (recoveryDiagnostics).
        const staleToken = failedAccessToken;
        let adoptedBeforeRefresh = false;

        try {
          const mfeRefreshHandler = getTokenRefreshHandler();

          let accessToken, refreshToken;

          if (mfeRefreshHandler) {
            // MFE mode — delegate refresh to StudentPortal. The endpoint is
            // for its logout log only.
            const result = await mfeRefreshHandler({
              endpoint: requestPath(originalRequest),
            });
            accessToken = result.accessToken;
            refreshToken = result.refreshToken;
          } else {
            // Standalone mode — refresh directly
            if (!getTokenObj()) {
              // Tokens already gone (e.g. cleared by a concurrent sessionExpired call).
              // No point refreshing — just end the session cleanly.
              onRefreshFailed(error);
              sessionExpired(LOGOUT_REASONS.NO_LOCAL_SESSION, {
                endpoint: requestPath(originalRequest),
              });
              return Promise.reject(error);
            }
            const sentToken = requestAccessToken(originalRequest);
            ({ accessToken, refreshToken } = await withRefreshLock(async () => {
              // Another tab of this origin may have refreshed while this one waited
              // for the lock (or before this request's 401 arrived): its pair is in
              // localStorage. Use it — a second refresh would only race that tab's.
              const stored = getTokenObj();
              if (
                stored?.ACCESS_TOKEN &&
                sentToken &&
                stored.ACCESS_TOKEN !== sentToken &&
                isNewerToken(stored.ACCESS_TOKEN, sentToken) &&
                tokenUserId(stored.ACCESS_TOKEN) === tokenUserId(sentToken) &&
                isTokenFresh(stored.ACCESS_TOKEN)
              ) {
                failedAccessToken = stored.ACCESS_TOKEN;
                logSessionRecovered({
                  stage: "sibling-tab",
                  source: "localStorage",
                  refreshPath: "api-client",
                  staleToken,
                });
                return {
                  accessToken: stored.ACCESS_TOKEN,
                  refreshToken: stored.REFRESH_TOKEN,
                };
              }

              if (!stored) {
                // Another tab ended the session while this one waited for the lock.
                throw Object.assign(new Error("No local session to refresh"), {
                  noLocalSession: true,
                });
              }
              const tokenObj = stored;
              // Stale tab: the other app already rotated this session and holds
              // the live pair. Refresh with THAT pair instead of ours, whose
              // refresh token cy-sso will reject. The adopted pair still goes
              // through the refresh rather than straight onto the retry: its
              // access token may have expired too (the other app rotated over
              // an hour ago), and cy-sso hands a still-valid pair back unchanged.
              const newer =
                staleTabRecoveryOn() && failedAccessToken
                  ? await adoptTokensFromSiblingContext(failedAccessToken, 1)
                  : null;
              if (newer) {
                failedAccessToken = newer.accessToken;
                adoptedBeforeRefresh = true;
              }
              const refreshed = await refreshTokens(
                newer
                  ? {
                      ...tokenObj,
                      ACCESS_TOKEN: newer.accessToken,
                      REFRESH_TOKEN: newer.refreshToken,
                    }
                  : tokenObj
              );
              if (newer) {
                logSessionRecovered({
                  stage: "before-refresh",
                  source: newer.source,
                  refreshPath: "api-client",
                  staleToken,
                });
              }

              // Persist through the common saveSession — updates sandbox_user,
              // the shared cookies + cy_auth_sync flag, refreshes the Axios
              // default headers (API.updateTokens), and dispatches
              // sandbox_user_update. Pass the previous user so profile fields
              // (role, email, profilePicUrl, isPaid, …) are preserved. Done
              // before the lock is released, so the next tab finds the pair.
              const prevUser = getTokenObj() || {};
              saveSession({
                accessToken: refreshed.accessToken,
                refreshToken: refreshed.refreshToken,
                userData: prevUser,
              });
              return refreshed;
            }));
          }

          originalRequest.headers["Authorization"] = `Bearer ${accessToken}`;
          originalRequest.headers["refresh-token"] = refreshToken;
          onRefreshed(accessToken, refreshToken);
          return this.axios(originalRequest);
        } catch (refreshError) {
          if (refreshError.noLocalSession) {
            onRefreshFailed(error);
            sessionExpired(LOGOUT_REASONS.NO_LOCAL_SESSION, {
              endpoint: requestPath(originalRequest),
            });
            return Promise.reject(error);
          }
          // Lost refresh race → adopt the winning context's tokens instead of
          // failing. Skipped when the MFE coordinator already ran this exact
          // recovery on this page (raceRecoveryAttempted).
          const raceLost =
            isRefreshRaceLost(refreshError) && !refreshError.raceRecoveryAttempted;
          // cy-sso rejected the refresh token outright. Behind the flag, check
          // the sibling stores once more before believing the session is dead
          // (the other app may have rotated between the check above and this
          // call landing). Skipped when the portal's coordinator already ran
          // the expiry flow (sessionExpiredHandled).
          const refreshRejected =
            staleTabRecoveryOn() &&
            refreshError.response?.status === 401 &&
            !refreshError.sessionExpiredHandled;
          if ((raceLost || refreshRejected) && failedAccessToken) {
            const adopted = await adoptTokensFromSiblingContext(
              failedAccessToken,
              raceLost ? RACE_RECOVERY_ATTEMPTS : 1
            );
            if (adopted) {
              logSessionRecovered({
                stage: raceLost ? "race-lost" : "after-reject",
                source: adopted.source,
                refreshPath: "api-client",
                staleToken,
              });
              // Recovered without a logout — success path, but worth a trail.
              Sentry.addBreadcrumb({
                category: "auth",
                level: "info",
                message: raceLost
                  ? "adopted sibling-context tokens after lost refresh race"
                  : "adopted sibling-context tokens after rejected refresh",
              });
              originalRequest.headers["Authorization"] = `Bearer ${adopted.accessToken}`;
              originalRequest.headers["refresh-token"] = adopted.refreshToken;
              onRefreshed(adopted.accessToken, adopted.refreshToken);
              return this.axios(originalRequest);
            }
          }
          logAuthError("token-refresh", refreshError);
          console.error("Token refresh failed:", refreshError);
          onRefreshFailed(refreshError);
          if (isMobileApp()) {
            return Promise.reject(refreshError);
          }
          // Log out only on a GENUINE refresh failure — the server responded
          // (401 invalid tokens, 500, or 200 with valid:false). A pure
          // connectivity error (offline / no `response`) keeps the session, so a
          // user who just lost internet is never logged out. sessionExpiredHandled
          // is set by the portal's doMfeTokenRefresh coordinator in MFE mode.
          if (
            isGenuineRefreshFailure(refreshError) &&
            !refreshError.sessionExpiredHandled &&
            !sessionEnded
          ) {
            sessionEnded = true;
            sessionExpired(LOGOUT_REASONS.REFRESH_FAILED, {
              ...refreshFailureDetails(refreshError),
              staleTabRecovery: staleTabRecoveryOn(),
              endpoint: requestPath(originalRequest),
              // Taken now, while the stores still hold what recovery saw.
              ...recoveryCheckDetails(failedAccessToken, { adoptedBeforeRefresh }),
            });
          }
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      // Post-refresh 401: the refresh succeeded (we have a fresh token) but THIS
      // request is STILL unauthorized. With a new token every API should work, so
      // a 401 here means the session is genuinely dead → log out immediately. No
      // tolerance, no counting. Runs in both standalone and embedded (MFE) mode so
      // a dead session never leaves a page stuck on a spinner.
      if (
        error.response?.status === 401 &&
        originalRequest?._retry &&
        !isMobileApp() &&
        !error.sessionExpiredHandled &&
        !sessionEnded
      ) {
        sessionEnded = true;
        sessionExpired(LOGOUT_REASONS.UNAUTHORIZED_AFTER_REFRESH, {
          endpoint: requestPath(originalRequest),
        });
      }

      return Promise.reject(error);
    }
  );
};

createApiClient.prototype.getApiBase = function () {
  return this.apiBase;
};

createApiClient.prototype.get = function (url, params = {}, data = {}, { cancelKey } = {}) {
  if (cancelKey && cancelTokensMap[cancelKey]) {
    cancelTokensMap[cancelKey].cancel("Cancelled due to new request");
  }

  let cancelTokenSource;
  if (cancelKey) {
    cancelTokenSource = Axios.CancelToken.source();
    cancelTokensMap[cancelKey] = cancelTokenSource;
  }

  return this.axios({
    method: "get",
    url,
    params,
    data,
    cancelToken: cancelTokenSource ? cancelTokenSource.token : undefined,
  });
};

createApiClient.prototype.post = function (url, data = {}, params = {}, headers = {}) {
  const addHeaders = Object.keys(headers).length ? { ...headers } : {};
  return this.axios({
    method: "post",
    url,
    data,
    params,
    ...addHeaders,
  });
};

createApiClient.prototype.put = function (url, data, params = {}) {
  return this.axios({
    method: "put",
    url,
    data,
    params,
  });
};

createApiClient.prototype.delete = function (url, data) {
  return this.axios({
    method: "delete",
    url,
    data,
  });
};
createApiClient.prototype.updateTokens = function (data) {
  this.axios.defaults.headers.common["Authorization"] =
    `Bearer ${data.ACCESS_TOKEN}`;
  this.axios.defaults.headers.common["refresh-token"] = data.REFRESH_TOKEN;
};

export { createApiClient };
